// OverviewReport — the "executive dashboard" view.
//
// Pulls reservations + rooms + guests and surfaces:
//   • Headline KPIs (reservations in window, in-house now, occupancy %,
//     gross revenue, average rate, average stay length)
//   • Reservation status pie (pending / confirmed / checked_in / out / cancelled)
//   • Revenue-by-day line chart
//   • Recent reservations table (last 10 in window)

import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import ReportShell from "./ReportShell";
import { StatTile, PieChart, LineChart } from "../../../components/charts";
import { reservationsUrl, roomsUrl, guestsUrl } from "../../../BaseURL";

const STATUS_COLORS = {
  pending:     "#3a7bd5",
  confirmed:   "#c8a05a",
  checked_in:  "#4a7c59",
  checked_out: "#8a7060",
  cancelled:   "#a93226",
  no_show:     "#7a4a4a",
};

export default function OverviewReport({ range, onRangeChange }) {
  const [reservations, setReservations] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [r, rm, g] = await Promise.all([
          axios.get(reservationsUrl, {
            params: { from: range.from || undefined, to: range.to || undefined },
          }),
          axios.get(roomsUrl),
          axios.get(guestsUrl),
        ]);
        if (cancelled) return;
        setReservations(r.data?.results || []);
        setRooms(rm.data?.results || []);
        setGuests(g.data?.results || []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [range.from, range.to]);

  const stats = useMemo(() => computeStats(reservations, rooms, guests), [reservations, rooms, guests]);

  // Excel export — recent reservations table.
  const csv = useMemo(() => ({
    headers: ["Code", "Guest", "Room", "Check-in", "Check-out", "Status", "Total (GH₵)", "Paid (GH₵)", "Balance (GH₵)"],
    rows: reservations.slice(0, 200).map(r => [
      r.code, r.guest_name, r.room_number,
      r.check_in_at || r.check_in_date,
      r.check_out_at || r.check_out_date,
      r.status,
      Number(r.total_amount).toFixed(2),
      Number(r.paid_amount).toFixed(2),
      Number(r.balance).toFixed(2),
    ]),
  }), [reservations]);

  return (
    <ReportShell
      title="Overview"
      subtitle="System-wide health and activity"
      range={{ ...range, onChange: onRangeChange }}
      csv={csv}
      filename="overview"
    >
      {loading
        ? <Loading/>
        : (
          <>
            {/* KPI tiles */}
            <div className="kpi-row">
              <StatTile icon="📋" label="Reservations" value={stats.totalReservations}
                sub={`${stats.confirmedCount} confirmed`} />
              <StatTile icon="🛏️" label="In House Now" value={stats.inHouseCount}
                sub={`${stats.dueOutCount} due to leave`} accent="#4a7c59" />
              <StatTile icon="🏨" label="Occupancy" value={`${stats.occupancyPct}%`}
                sub={`${stats.occupiedRooms}/${stats.totalRooms} rooms`} accent="#3a7bd5" />
              <StatTile icon="💰" label="Revenue" value={fmtMoney(stats.revenue)}
                sub={`Paid in this period`} accent="#c8a05a" />
              <StatTile icon="📊" label="Avg Rate / Night" value={fmtMoney(stats.avgRate)}
                sub={`across ${stats.totalReservations} bookings`} accent="#a8814a" />
              <StatTile icon="👥" label="Unique Guests" value={stats.uniqueGuests}
                sub={`${guests.length} total in database`} accent="#8a6a30" />
            </div>

            {/* Two-up: status pie + revenue line */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18, marginTop: 16 }}>
              <Panel title="Reservation Status Breakdown">
                <PieChart data={stats.statusBreakdown} />
              </Panel>
              <Panel title="Revenue by Day">
                <LineChart data={stats.revenueByDay} valueFormat={(n) => `GH₵ ${Math.round(n)}`} />
              </Panel>
            </div>

            {/* Recent reservations */}
            <Panel title="Recent Reservations" style={{ marginTop: 18 }}>
              <table className="tbl" style={{ width: "100%" }}>
                <thead>
                  <tr>
                    <th>Code</th><th>Guest</th><th>Room</th>
                    <th>Check-in</th><th>Check-out</th>
                    <th>Status</th><th style={{ textAlign: "right" }}>Total</th>
                    <th style={{ textAlign: "right" }}>Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {reservations.slice(0, 10).map(r => (
                    <tr key={r.id}>
                      <td style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 15 }}>{r.code}</td>
                      <td>{r.guest_name}</td>
                      <td>{r.room_number || "—"}</td>
                      <td>{fmtDate(r.check_in_at || r.check_in_date)}</td>
                      <td>{fmtDate(r.check_out_at || r.check_out_date)}</td>
                      <td><StatusBadge status={r.status} /></td>
                      <td style={{ textAlign: "right" }}>{fmtMoney(r.total_amount)}</td>
                      <td style={{ textAlign: "right", color: Number(r.balance) > 0 ? "#a93226" : "#2d6a4f" }}>
                        {fmtMoney(r.balance)}
                      </td>
                    </tr>
                  ))}
                  {reservations.length === 0 && (
                    <tr><td colSpan={8} style={{ textAlign: "center", padding: 24, color: "#8a7060", fontStyle: "italic" }}>
                      No reservations in this range.
                    </td></tr>
                  )}
                </tbody>
              </table>
            </Panel>
          </>
        )}
    </ReportShell>
  );
}

// ─── Shared bits used by every report ───────────────────────────────────
export function Panel({ title, children, style }) {
  return (
    <div style={{
      border: "1px solid rgba(180,140,80,.22)",
      borderRadius: 10,
      background: "#fff",
      overflow: "hidden",
      ...style,
    }}>
      <div style={{
        padding: "10px 14px",
        borderBottom: "1px solid rgba(180,140,80,.18)",
        fontFamily: "'Cormorant Garamond', serif",
        fontSize: 17, color: "#2c1f14", fontWeight: 500,
        background: "linear-gradient(180deg, #fdf8f2 0%, #fcf3e6 100%)",
      }}>
        {title}
      </div>
      <div style={{ padding: 14 }}>{children}</div>
    </div>
  );
}

export function StatusBadge({ status }) {
  const color = STATUS_COLORS[status] || "#8a7060";
  return (
    <span style={{
      display: "inline-block", padding: "3px 8px", borderRadius: 999,
      background: `${color}22`, color, fontSize: 10.5, fontWeight: 500,
      letterSpacing: ".08em", textTransform: "uppercase",
    }}>{String(status || "").replace("_", " ")}</span>
  );
}

export function Loading() {
  return (
    <div style={{ padding: 60, textAlign: "center", color: "#8a7060", fontSize: 13.5 }}>
      Loading report…
    </div>
  );
}

export const fmtMoney = (n) => `GH₵ ${Number(n || 0).toFixed(2)}`;
export const fmtDate  = (iso) => iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : "—";

// ─── Stats math ─────────────────────────────────────────────────────────
function computeStats(reservations, rooms, guests) {
  const totalReservations = reservations.length;
  const inHouseCount      = reservations.filter(r => r.status === "checked_in").length;
  const confirmedCount    = reservations.filter(r => r.status === "confirmed").length;

  const today    = new Date().toISOString().slice(0, 10);
  const dueOutCount = reservations
    .filter(r => r.status === "checked_in")
    .filter(r => String(r.check_out_at || r.check_out_date || "").slice(0, 10) <= today)
    .length;

  const totalRooms     = rooms.filter(r => r.is_active === 1).length;
  const occupiedRooms  = rooms.filter(r => r.status === "occupied").length;
  const occupancyPct   = totalRooms > 0 ? Math.round((occupiedRooms / totalRooms) * 100) : 0;

  const revenue        = reservations.reduce((s, r) => s + (Number(r.paid_amount) || 0), 0);
  const avgRate        = totalReservations > 0
    ? reservations.reduce((s, r) => s + (Number(r.rate_per_night || r.rate_per_day || r.rate_per_hour) || 0), 0) / totalReservations
    : 0;

  const uniqueGuests   = new Set(reservations.map(r => r.guest_id)).size;

  // Status breakdown for the pie.
  const STATUSES = ["pending","confirmed","checked_in","checked_out","cancelled","no_show"];
  const statusBreakdown = STATUSES
    .map(s => ({ label: s.replace("_", " "), value: reservations.filter(r => r.status === s).length, color: STATUS_COLORS[s] }))
    .filter(s => s.value > 0);

  // Revenue by day — bucket paid_amount by check_in_at date.  Real
  // accounting would use the payment timestamp; for the executive view
  // bucketing by check-in is plenty.
  const buckets = new Map();
  for (const r of reservations) {
    const day = String(r.check_in_at || r.check_in_date || "").slice(0, 10);
    if (!day) continue;
    buckets.set(day, (buckets.get(day) || 0) + (Number(r.paid_amount) || 0));
  }
  const revenueByDay = Array.from(buckets.entries())
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([day, value]) => ({
      label: new Date(`${day}T00:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "short" }),
      value,
    }));

  return {
    totalReservations, inHouseCount, confirmedCount, dueOutCount,
    totalRooms, occupiedRooms, occupancyPct,
    revenue, avgRate, uniqueGuests,
    statusBreakdown, revenueByDay,
  };
}
